import React from 'react';
import styled from '@emotion/styled';
import { Palette, BarChart3, Languages, Target } from 'lucide-react';

const MainContainer = styled.div`
  position: relative;
  margin: 80px auto;
  width: 100%;
  max-width: 1400px;
  padding: 0 24px;
  z-index: 2;

  @media (max-width: 768px) {
    margin: 40px auto;
    padding: 0 16px;
  }
`;

// Mobile-first text section that shows on smaller screens
const MobileTextSection = styled.div`
  display: none;
  flex-direction: column;
  align-items: center;
  text-align: center;
  margin-bottom: 40px;
  padding: 0 16px;

  @media (max-width: 1200px) {
    display: flex;
  }
`;

// Mobile-optimized feature grid
const MobileFeatureGrid = styled.div`
  display: none;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  width: 100%;
  
  @media (max-width: 1200px) {
    display: grid;
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const MobileFeatureCard = styled.div`
  background: rgb(51, 51, 51);
  border-radius: 16px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  min-height: 120px;
  text-align: center;

  @media (max-width: 480px) {
    flex-direction: row;
    justify-content: flex-start;
    min-height: auto;
    padding: 16px;
    gap: 16px;
    text-align: left;
  }
`;

const MobileFeatureIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const MobileFeatureLabel = styled.div`
  color: #ffffff;
  font-size: 16px;
  font-weight: 500;
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, 'Helvetica Neue', sans-serif;

  @media (max-width: 480px) {
    font-size: 14px;
  }
`;

const ServiceGridContainer = styled.div`
  position: relative;
  margin: 0;
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 40px 0;
  right: 600px;

  @media (max-width: 1200px) {
    display: none;
  }
`;

const ServiceGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  width: 1256px;
  transform: rotate(-7.55deg) skew(10deg, 0deg);
  margin-right: 100px;
`;

const ServiceCard = styled.div<{ size?: 'large' | 'medium' | 'small' }>`
  background: rgb(51, 51, 51);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-radius: ${props => props.size === 'large' ? '12px' : '8px'};
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-shadow: ${props => props.size === 'large' ? '0 8px 32px rgba(0, 0, 0, 0.3)' : '0 4px 16px rgba(0, 0, 0, 0.2)'};
  position: relative;
  box-sizing: border-box;
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.1);
  
  &:hover {
    // background: rgba(51, 51, 51, 0.6);
    transform: translateY(-4px);
    box-shadow: ${props => props.size === 'large' ? '0 12px 40px rgba(0, 0, 0, 0.4)' : '0 8px 24px rgba(0, 0, 0, 0.3)'};
    border-color: rgba(255, 255, 255, 0.2);
  }
  
  ${props => {
    if (props.size === 'large') {
      return `width: calc(33.333% - 11px);`;
    } else if (props.size === 'medium') {
      return `width: calc(25% - 12px);`;
    } else {
      return `width: calc(33.333% - 11px);`;
    }
  }}
`;

const ServiceCardImage = styled.img`
  width: 48px;
  height: 48px;
  margin-bottom: 4px;
`;

const ServiceCardTitle = styled.div`
  color: #ffffff;
  font-size: 20px;
  font-weight: 500;
  line-height: 20px;
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, 'Helvetica Neue', sans-serif;
`;

const ServiceCardDescription = styled.div`
  color: #d6d6d6;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, 'Helvetica Neue', sans-serif;
`;

const ServiceIconCard = styled.div<{ size?: 'medium' | 'small' }>`
  gap: 8px;
  background: rgb(51, 51, 51);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-radius: 8px;
  padding: 16px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;
  height: 111px;
  
  &:hover {
    // background: rgba(51, 51, 51, 0.6);
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
    border-color: rgba(255, 255, 255, 0.2);
  }
  
  ${props => {
    if (props.size === 'medium') {
      return `width: calc(25% - 12px);`;
    } else {
      return `width: calc(33.333% - 11px);`;
    }
  }}
`;

const ServiceIcon = styled.svg`
  width: 48px;
  height: 48px;
  color: #ffffff;
  fill: currentColor;
  flex-shrink: 0;
`;

const ServiceIconLabel = styled.div`
  color: #ffffff;
  font-size: 20px;
  font-weight: 500;
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, 'Helvetica Neue', sans-serif;
  text-align: center;
`;

const ModelCard = styled.div<{ size?: 'medium' | 'small' }>`
  background: rgb(51, 51, 51);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;
  
  &:hover {
    // background: rgba(51, 51, 51, 0.6);
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
    border-color: rgba(255, 255, 255, 0.2);
  }
  
  ${props => {
    if (props.size === 'medium') {
      return `width: calc(25% - 12px);`;
    } else {
      return `width: calc(33.333% - 11px);`;
    }
  }}
`;

const ModelHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`;

const ModelIcon = styled.img`
  width: 18px;
  height: 18px;
`;

const ModelName = styled.span`
  color: #ffffff;
  font-size: 16px;
  font-weight: 600;
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, 'Helvetica Neue', sans-serif;
`;

const ModelSeparator = styled.span`
  color: #ffffff;
  font-size: 14px;
`;

const ModelStatus = styled.div<{ hot?: boolean; new?: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  background-color: ${props => props.hot ? '#7a2101' : props.new ? '#3f3682' : 'transparent'};
  color: ${props => props.hot ? '#f4bfab' : props.new ? '#d2ccf8' : '#ffffff'};
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, 'Helvetica Neue', sans-serif;
`;

const StatusIcon = styled.svg`
  width: 16px;
  height: 16px;
  fill: currentColor;
`;

const ModelMetadata = styled.div`
  display: flex;
  gap: 16px;
  color: #d6d6d6;
  font-size: 12px;
  font-weight: 400;
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, 'Helvetica Neue', sans-serif;
`;

const MetadataItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const MetadataIcon = styled.svg`
  width: 16px;
  height: 16px;
  fill: currentColor;
`;

const TextSection = styled.div`
  position: absolute;
  right: 80px;
  top: 38%;
  transform: translateY(-50%);
  max-width: 420px;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  z-index: 10;

  @media (max-width: 1200px) {
    display: none;
  }
`;

const MainTitle = styled.h2<{ mobile?: boolean }>`
  font-size: ${props => props.mobile ? '32px' : '45px'};
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 20px 0;
  line-height: 1.2;
  font-family: 'Space Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  text-align: ${props => props.mobile ? 'center' : 'left'};

  @media (max-width: 480px) {
    font-size: ${props => props.mobile ? '26px' : '45px'};
    margin: 0 0 16px 0;
  }
`;

const Subtitle = styled.p<{ mobile?: boolean }>`
  font-family: 'Space Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-size: ${props => props.mobile ? '16px' : '20px'};
  line-height: 1.6;
  color: #4b5563;
  margin: 0 0 16px 0;
  font-weight: 400;
  text-align: ${props => props.mobile ? 'center' : 'left'};
  max-width: ${props => props.mobile ? '600px' : 'none'};

  @media (max-width: 480px) {
    font-size: ${props => props.mobile ? '14px' : '18px'};
  }
`;

const Disclaimer = styled.div<{ mobile?: boolean }>`
  font-size: 13px;
  color: #999;
  font-style: italic;
  text-align: ${props => props.mobile ? 'center' : 'left'};

  @media (max-width: 480px) {
    font-size: 12px;
  }
`;

const ButtonContainer = styled.div<{ mobile?: boolean }>`
  display: flex;
  flex-direction: row;
  gap: 12px;
  margin-top: 24px;
  justify-content: ${props => props.mobile ? 'center' : 'flex-start'};
  flex-wrap: wrap;

  @media (max-width: 480px) {
    flex-direction: column;
    width: 100%;
    gap: 10px;
    margin-top: 20px;
  }
`;

const GetStartedButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 48px;
  padding: 0 24px;
  background: #0b0f1a;
  color: white;
  text-decoration: none;
  font-weight: 600;
  font-size: 16px;
  border-radius: 12px;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(11, 15, 26, 0.15);
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, 'Helvetica Neue', sans-serif;
  white-space: nowrap;
  min-width: 140px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(11, 15, 26, 0.25);
    background: #1a1f2e;
  }

  @media (max-width: 480px) {
    width: 100%;
    height: 44px;
    font-size: 14px;
  }
`;

const EnquireButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 48px;
  padding: 0 24px;
  background: #ddd6fe;
  color: #000000ff;
  text-decoration: none;
  font-weight: 600;
  font-size: 15px;
  border-radius: 12px;
  border: 2px solid #ddd6fe;
  transition: all 0.2s ease;
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, Roboto, 'Helvetica Neue', sans-serif;
  white-space: nowrap;
  min-width: 160px;

  &:hover {
    transform: translateY(-2px);
    background: #ddd6fe;
    color: #0b0f1a;
    box-shadow: 0 8px 20px rgba(221, 214, 254, 0.4);
  }

  @media (max-width: 480px) {
    width: 100%;
    height: 44px;
    font-size: 14px;
  }
`;

// Mobile feature data
const mobileFeatures = [
  { icon: Languages, label: 'Multilingual Support', color: '#60A5FA' },
  { icon: Target, label: 'Brand Awareness', color: '#F59E0B' },
  { icon: BarChart3, label: 'Advanced Reporting', color: '#8B5CF6' },
  { icon: Palette, label: 'Appearance Customization', color: '#f43f5e' },
];

const AzureServiceGrid: React.FC = () => {
  return (
    <MainContainer id="features">
      {/* Mobile Text Section - shown only on mobile/tablet */}
      <MobileTextSection>
        <MainTitle mobile>Your pages are the answer. We just make them talk.</MainTitle>
        <Subtitle mobile>From multilingual support to order tracking, SmushLabs brings together the essentials of modern AI chatbots. Built for small businesses that need clarity, not complexity.</Subtitle>
        <Disclaimer mobile>*Integrations rolling out soon</Disclaimer>
        <ButtonContainer mobile>
          <GetStartedButton href="/get-started">
            Get Started
          </GetStartedButton>
          <EnquireButton href="/get-started">
            Enquire Features
          </EnquireButton>
        </ButtonContainer>
      </MobileTextSection>

      {/* Mobile Feature Grid - shown only on mobile/tablet */}
      <MobileFeatureGrid>
        {mobileFeatures.map((feature, index) => (
          <MobileFeatureCard key={index}>
            <MobileFeatureIcon>
              <feature.icon size={36} color={feature.color} />
            </MobileFeatureIcon>
            <MobileFeatureLabel>{feature.label}</MobileFeatureLabel>
          </MobileFeatureCard>
        ))}
      </MobileFeatureGrid>

      {/* Desktop ServiceGrid - hidden on mobile */}
      <ServiceGridContainer>
        <ServiceGrid>
          {/* Row 1: 3 Large Cards (33% each) */}
          {/* Azure AI Foundry Agent Service */}

          {/* Empty large card */}
          <ServiceCard />

          <ServiceCard size="large">
            <ServiceCardImage src="/assets/AgentService-70447390.svg" alt="Azure AI Foundry Agent Service" />
            <ServiceCardTitle>Azure AI Foundry Agent Service</ServiceCardTitle>
            <ServiceCardDescription>
              A fully-managed service to help you build, deploy, and scale fast, secure, and extensible AI agents.
            </ServiceCardDescription>
          </ServiceCard>

          {/* Azure AI Foundry Models */}
          <ServiceCard size="large">
            <ServiceCardImage src="https://www.smushlabs.ai/favicon.ico" alt="Azure AI Foundry Models" />
            <ServiceCardTitle>wesbite-in-a-widget</ServiceCardTitle>
            <ServiceCardDescription>
              leverage your website content to help users navigate and answer queries.
            </ServiceCardDescription>
          </ServiceCard>



          {/* Rows 2-3: 4 Medium Cards (25% each) */}
          {/* Vision + Document */}

          <ServiceIconCard size="medium" />

          <ServiceIconCard size="medium">
            <ServiceIcon viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
              <path d="M10 3c-1.53 0-2.8.4-3.82 1H10a6.5 6.5 0 0 1 5.3 2.62 8.73 8.73 0 0 1 1.38 2.62c.05.14.06.22.07.25v.01a6.5 6.5 0 0 1-.32.94c-.24.57-.6 1.26-1.14 1.94A6.5 6.5 0 0 1 10 15H6.18c1.03.6 2.29 1 3.82 1a7.44 7.44 0 0 0 6.08-3 9.74 9.74 0 0 0 1.56-2.96c.06-.2.11-.4.11-.54s-.05-.35-.1-.54A9.73 9.73 0 0 0 16.08 6 7.44 7.44 0 0 0 10 3Zm0 3H3.92c.27-.34.57-.68.92-1H10v1Zm0 7a3.5 3.5 0 1 0 0-7v1H3.24c-.21.35-.38.7-.52 1H8a2.5 2.5 0 1 1 0 3H2.72c.14.3.3.65.52 1H10v1Zm0 0H3.92c.27.34.57.68.92 1H10v-1ZM7.55 9h-5.2c-.06.18-.1.37-.1.5s.04.32.1.5h5.2a2.51 2.51 0 0 1 0-1Z" fill="currentColor"></path>
            </ServiceIcon>
            <ServiceIconLabel>Vision + Document</ServiceIconLabel>
          </ServiceIconCard>

          {/* Multilingual Support */}
          <ServiceIconCard size="medium">
            <Languages size={58} color="#60A5FA" />
            <ServiceIconLabel>Multilingual Support</ServiceIconLabel>
          </ServiceIconCard>

          {/* Brand Awareness */}
          <ServiceIconCard size="medium">
            <Target size={58} color="#F59E0B" />
            <ServiceIconLabel>Brand Awareness</ServiceIconLabel>
          </ServiceIconCard>

          <ServiceIconCard size="medium" />

          {/* Azure Machine Learning */}
          <ServiceIconCard size="medium">
            <ServiceCardImage src="/assets/MachineLearning-62fde782.svg" alt="Azure Machine Learning" style={{ width: '48px', height: '48px', objectFit: 'contain' }} />
            <ServiceIconLabel>Azure Machine Learning</ServiceIconLabel>
          </ServiceIconCard>

          {/* Powerful Analytics (replacing Azure AI Search) */}
          <ServiceIconCard size="medium">
            <BarChart3 size={48} color="#8B5CF6" />
            <ServiceIconLabel>Advanced reporting</ServiceIconLabel>
          </ServiceIconCard>

          {/* Appearance Customization (replacing Azure AI Content Safety) */}
          <ServiceIconCard size="medium">
            <Palette size={58} color="#f43f5e" />
            <ServiceIconLabel>Appearance Customization</ServiceIconLabel>
          </ServiceIconCard>


          {/* Rows 4-5: 3 Small Cards (33% each) */}
          <ModelCard size="small">
            <ModelHeader>
              <ModelName>Llama-2-7b</ModelName>
              <ModelSeparator>•</ModelSeparator>
              <ModelStatus hot>
                <StatusIcon viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8.17 2.38c.28-.14.55-.24.8-.3.02.54.18 1.08.41 1.59a16.63 16.63 0 0 0 1.32 2.25c.46.7.9 1.38 1.24 2.06.35.7.56 1.38.56 2.02 0 1.15-.34 2.15-.99 2.85a3.92 3.92 0 0 1-3 1.15c-1.4 0-2.37-.4-3.05-1.03a4.84 4.84 0 0 1-1-5.43l.13.25c.38.76 1.3 1.07 2.07.7.84-.43 1.08-1.47.72-2.24-.37-.81-.66-1.81-.4-2.6.2-.58.65-1 1.19-1.27ZM4.1 6.18v.01l-.02.03a3.08 3.08 0 0 0-.22.33 5.33 5.33 0 0 0-.74 4.06c.26 1.19.77 2.3 1.65 3.1A5.3 5.3 0 0 0 8.5 15c1.62 0 2.89-.54 3.75-1.47A5.12 5.12 0 0 0 13.5 10c0-.86-.28-1.69-.67-2.46a21.42 21.42 0 0 0-1.35-2.25c-.47-.73-.9-1.4-1.19-2.03A2.77 2.77 0 0 1 10 1.6.5.5 0 0 0 9.5 1c-.42 0-1.12.15-1.79.5a3.3 3.3 0 0 0-1.68 1.84c-.4 1.17.06 2.5.44 3.32.17.38.02.79-.26.93a.54.54 0 0 1-.73-.24l-.53-1.07a.5.5 0 0 0-.84-.1Z" fill="currentColor"></path>
                </StatusIcon>
                Hot
              </ModelStatus>
            </ModelHeader>
            <ModelMetadata>
              <MetadataItem>
                <MetadataIcon viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5.5 5a.5.5 0 0 0 0 1h5a.5.5 0 0 0 0-1h-5Zm0 2.5a.5.5 0 0 0 0 1h3a.5.5 0 0 0 0-1h-3Zm0 2.5a.5.5 0 0 0 0 1h5a.5.5 0 0 0 0-1h-5Zm-1-8A2.5 2.5 0 0 0 2 4.5v7A2.5 2.5 0 0 0 4.5 14h7a2.5 2.5 0 0 0 2.5-2.5v-7A2.5 2.5 0 0 0 11.5 2h-7ZM3 4.5C3 3.67 3.67 3 4.5 3h7c.83 0 1.5.67 1.5 1.5v7c0 .83-.67 1.5-1.5 1.5h-7A1.5 1.5 0 0 1 3 11.5v-7Z" fill="currentColor"></path>
                </MetadataIcon>
                Text-generation
              </MetadataItem>
              <MetadataItem>
                <MetadataIcon viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
                  <path d="M7.2 2.1a.9.9 0 0 1 1.6 0l1.53 3.08 3.4.5a.9.9 0 0 1 .5 1.53l-2.46 2.4.58 3.39a.9.9 0 0 1-1.3.95L8 12.35l-3.04 1.6a.9.9 0 0 1-1.3-.95l.57-3.39-2.46-2.4a.9.9 0 0 1 .5-1.53l3.4-.5L7.2 2.1Zm.8.63L6.55 5.67a.9.9 0 0 1-.68.5l-3.25.47 2.35 2.3c.21.2.3.5.26.79l-.56 3.24 2.91-1.53a.9.9 0 0 1 .84 0l2.9 1.53-.55-3.24a.9.9 0 0 1 .26-.8l2.36-2.3-3.26-.46a.9.9 0 0 1-.67-.5L8 2.73Z" fill="currentColor"></path>
                </MetadataIcon>
                561
              </MetadataItem>
              <MetadataItem>
                <MetadataIcon viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5.75 3.06A.5.5 0 0 0 5 3.5v9c0 .38.41.62.75.44l8-4.5a.5.5 0 0 0 0-.88l-8-4.5ZM4 3.5a1.5 1.5 0 0 1 2.24-1.3l8 4.5a1.5 1.5 0 0 1 0 2.6l-8 4.5A1.5 1.5 0 0 1 4 12.5v-9Z" fill="currentColor"></path>
                </MetadataIcon>
                154
              </MetadataItem>
            </ModelMetadata>
          </ModelCard>

          <ModelCard size="small">
            <ModelHeader>
              <ModelIcon src="/assets/Deepseek-dark-e556373f.svg" alt="Deepseek" />
              <ModelName>Deepseek-R1</ModelName>
              <ModelSeparator>•</ModelSeparator>
              <ModelStatus hot>
                <StatusIcon viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8.17 2.38c.28-.14.55-.24.8-.3.02.54.18 1.08.41 1.59a16.63 16.63 0 0 0 1.32 2.25c.46.7.9 1.38 1.24 2.06.35.7.56 1.38.56 2.02 0 1.15-.34 2.15-.99 2.85a3.92 3.92 0 0 1-3 1.15c-1.4 0-2.37-.4-3.05-1.03a4.84 4.84 0 0 1-1-5.43l.13.25c.38.76 1.3 1.07 2.07.7.84-.43 1.08-1.47.72-2.24-.37-.81-.66-1.81-.4-2.6.2-.58.65-1 1.19-1.27ZM4.1 6.18v.01l-.02.03a3.08 3.08 0 0 0-.22.33 5.33 5.33 0 0 0-.74 4.06c.26 1.19.77 2.3 1.65 3.1A5.3 5.3 0 0 0 8.5 15c1.62 0 2.89-.54 3.75-1.47A5.12 5.12 0 0 0 13.5 10c0-.86-.28-1.69-.67-2.46a21.42 21.42 0 0 0-1.35-2.25c-.47-.73-.9-1.4-1.19-2.03A2.77 2.77 0 0 1 10 1.6.5.5 0 0 0 9.5 1c-.42 0-1.12.15-1.79.5a3.3 3.3 0 0 0-1.68 1.84c-.4 1.17.06 2.5.44 3.32.17.38.02.79-.26.93a.54.54 0 0 1-.73-.24l-.53-1.07a.5.5 0 0 0-.84-.1Z" fill="currentColor"></path>
                </StatusIcon>
                Hot
              </ModelStatus>
            </ModelHeader>
            <ModelMetadata>
              <MetadataItem>
                <MetadataIcon viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5.5 5a.5.5 0 0 0 0 1h5a.5.5 0 0 0 0-1h-5Zm0 2.5a.5.5 0 0 0 0 1h3a.5.5 0 0 0 0-1h-3Zm0 2.5a.5.5 0 0 0 0 1h5a.5.5 0 0 0 0-1h-5Zm-1-8A2.5 2.5 0 0 0 2 4.5v7A2.5 2.5 0 0 0 4.5 14h7a2.5 2.5 0 0 0 2.5-2.5v-7A2.5 2.5 0 0 0 11.5 2h-7ZM3 4.5C3 3.67 3.67 3 4.5 3h7c.83 0 1.5.67 1.5 1.5v7c0 .83-.67 1.5-1.5 1.5h-7A1.5 1.5 0 0 1 3 11.5v-7Z" fill="currentColor"></path>
                </MetadataIcon>
                Text-generation
              </MetadataItem>
              <MetadataItem>
                <MetadataIcon viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
                  <path d="M7.2 2.1a.9.9 0 0 1 1.6 0l1.53 3.08 3.4.5a.9.9 0 0 1 .5 1.53l-2.46 2.4.58 3.39a.9.9 0 0 1-1.3.95L8 12.35l-3.04 1.6a.9.9 0 0 1-1.3-.95l.57-3.39-2.46-2.4a.9.9 0 0 1 .5-1.53l3.4-.5L7.2 2.1Zm.8.63L6.55 5.67a.9.9 0 0 1-.68.5l-3.25.47 2.35 2.3c.21.2.3.5.26.79l-.56 3.24 2.91-1.53a.9.9 0 0 1 .84 0l2.9 1.53-.55-3.24a.9.9 0 0 1 .26-.8l2.36-2.3-3.26-.46a.9.9 0 0 1-.67-.5L8 2.73Z" fill="currentColor"></path>
                </MetadataIcon>
                561
              </MetadataItem>
              <MetadataItem>
                <MetadataIcon viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5.75 3.06A.5.5 0 0 0 5 3.5v9c0 .38.41.62.75.44l8-4.5a.5.5 0 0 0 0-.88l-8-4.5ZM4 3.5a1.5 1.5 0 0 1 2.24-1.3l8 4.5a1.5 1.5 0 0 1 0 2.6l-8 4.5A1.5 1.5 0 0 1 4 12.5v-9Z" fill="currentColor"></path>
                </MetadataIcon>
                154
              </MetadataItem>
            </ModelMetadata>
          </ModelCard>

          <ModelCard size="small">
            <ModelHeader>
              <ModelIcon src="/assets/AzureOpenAI-1ebfb35b.svg" alt="OpenAI" />
              <ModelName>GPT-4.1</ModelName>
              <ModelSeparator>•</ModelSeparator>
              <ModelStatus new>
                <StatusIcon viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5.47 9.83a.92.92 0 0 0 1.4-.46l.35-1.07A1.72 1.72 0 0 1 8.3 7.22l1.09-.35a.92.92 0 0 0-.04-1.75l-1.07-.35A1.7 1.7 0 0 1 7.2 3.7L6.85 2.6a.92.92 0 0 0-.87-.6c-.2 0-.38.05-.53.16a.92.92 0 0 0-.35.46l-.35 1.09A1.71 1.71 0 0 1 3.7 4.77l-1.08.36a.92.92 0 0 0 0 1.74l1.08.35A1.71 1.71 0 0 1 4.78 8.3l.35 1.08c.06.18.18.34.34.45ZM4 6.26 3.15 6l.87-.28A2.72 2.72 0 0 0 5.7 4l.26-.86.28.87a2.7 2.7 0 0 0 1.72 1.71l.88.27-.86.28a2.7 2.7 0 0 0-1.72 1.72l-.26.85-.28-.86A2.7 2.7 0 0 0 4 6.26Zm6.52 7.6c.14.09.3.14.47.14a.81.81 0 0 0 .76-.55l.25-.76a1.09 1.09 0 0 1 .68-.68l.77-.25a.8.8 0 0 0-.02-1.53l-.77-.25a1.08 1.08 0 0 1-.68-.67l-.25-.78A.8.8 0 0 0 11.1 8a.8.8 0 0 0-.88.54l-.25.77a1.1 1.1 0 0 1-.66.67l-.78.26a.8.8 0 0 0-.38 1.22c.1.14.23.24.4.3l.76.25a1.09 1.09 0 0 1 .68.68l.25.77c.06.16.16.3.3.4Zm-.91-2.8L9.44 11l.19-.06a2.09 2.09 0 0 0 1.3-1.32l.05-.18.06.18a2.08 2.08 0 0 0 1.32 1.32l.2.06-.18.06a2.08 2.08 0 0 0-1.32 1.32l-.06.18-.06-.18a2.07 2.07 0 0 0-1.32-1.32Z" fill="currentColor"></path>
                </StatusIcon>
                New
              </ModelStatus>
            </ModelHeader>
            <ModelMetadata>
              <MetadataItem>
                <MetadataIcon viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5.5 5a.5.5 0 0 0 0 1h5a.5.5 0 0 0 0-1h-5Zm0 2.5a.5.5 0 0 0 0 1h3a.5.5 0 0 0 0-1h-3Zm0 2.5a.5.5 0 0 0 0 1h5a.5.5 0 0 0 0-1h-5Zm-1-8A2.5 2.5 0 0 0 2 4.5v7A2.5 2.5 0 0 0 4.5 14h7a2.5 2.5 0 0 0 2.5-2.5v-7A2.5 2.5 0 0 0 11.5 2h-7ZM3 4.5C3 3.67 3.67 3 4.5 3h7c.83 0 1.5.67 1.5 1.5v7c0 .83-.67 1.5-1.5 1.5h-7A1.5 1.5 0 0 1 3 11.5v-7Z" fill="currentColor"></path>
                </MetadataIcon>
                Multimodal
              </MetadataItem>
              <MetadataItem>
                <MetadataIcon viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
                  <path d="M7.2 2.1a.9.9 0 0 1 1.6 0l1.53 3.08 3.4.5a.9.9 0 0 1 .5 1.53l-2.46 2.4.58 3.39a.9.9 0 0 1-1.3.95L8 12.35l-3.04 1.6a.9.9 0 0 1-1.3-.95l.57-3.39-2.46-2.4a.9.9 0 0 1 .5-1.53l3.4-.5L7.2 2.1Zm.8.63L6.55 5.67a.9.9 0 0 1-.68.5l-3.25.47 2.35 2.3c.21.2.3.5.26.79l-.56 3.24 2.91-1.53a.9.9 0 0 1 .84 0l2.9 1.53-.55-3.24a.9.9 0 0 1 .26-.8l2.36-2.3-3.26-.46a.9.9 0 0 1-.67-.5L8 2.73Z" fill="currentColor"></path>
                </MetadataIcon>
                561
              </MetadataItem>
              <MetadataItem>
                <MetadataIcon viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5.75 3.06A.5.5 0 0 0 5 3.5v9c0 .38.41.62.75.44l8-4.5a.5.5 0 0 0 0-.88l-8-4.5ZM4 3.5a1.5 1.5 0 0 1 2.24-1.3l8 4.5a1.5 1.5 0 0 1 0 2.6l-8 4.5A1.5 1.5 0 0 1 4 12.5v-9Z" fill="currentColor"></path>
                </MetadataIcon>
                154
              </MetadataItem>
            </ModelMetadata>
          </ModelCard>

          <ModelCard size="small">
            <ModelHeader>
              <ModelName>Llama-2-7b</ModelName>
              <ModelSeparator>•</ModelSeparator>
              <ModelStatus hot>
                <StatusIcon viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8.17 2.38c.28-.14.55-.24.8-.3.02.54.18 1.08.41 1.59a16.63 16.63 0 0 0 1.32 2.25c.46.7.9 1.38 1.24 2.06.35.7.56 1.38.56 2.02 0 1.15-.34 2.15-.99 2.85a3.92 3.92 0 0 1-3 1.15c-1.4 0-2.37-.4-3.05-1.03a4.84 4.84 0 0 1-1-5.43l.13.25c.38.76 1.3 1.07 2.07.7.84-.43 1.08-1.47.72-2.24-.37-.81-.66-1.81-.4-2.6.2-.58.65-1 1.19-1.27ZM4.1 6.18v.01l-.02.03a3.08 3.08 0 0 0-.22.33 5.33 5.33 0 0 0-.74 4.06c.26 1.19.77 2.3 1.65 3.1A5.3 5.3 0 0 0 8.5 15c1.62 0 2.89-.54 3.75-1.47A5.12 5.12 0 0 0 13.5 10c0-.86-.28-1.69-.67-2.46a21.42 21.42 0 0 0-1.35-2.25c-.47-.73-.9-1.4-1.19-2.03A2.77 2.77 0 0 1 10 1.6.5.5 0 0 0 9.5 1c-.42 0-1.12.15-1.79.5a3.3 3.3 0 0 0-1.68 1.84c-.4 1.17.06 2.5.44 3.32.17.38.02.79-.26.93a.54.54 0 0 1-.73-.24l-.53-1.07a.5.5 0 0 0-.84-.1Z" fill="currentColor"></path>
                </StatusIcon>
                Hot
              </ModelStatus>
            </ModelHeader>
            <ModelMetadata>
              <MetadataItem>
                <MetadataIcon viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5.5 5a.5.5 0 0 0 0 1h5a.5.5 0 0 0 0-1h-5Zm0 2.5a.5.5 0 0 0 0 1h3a.5.5 0 0 0 0-1h-3Zm0 2.5a.5.5 0 0 0 0 1h5a.5.5 0 0 0 0-1h-5Zm-1-8A2.5 2.5 0 0 0 2 4.5v7A2.5 2.5 0 0 0 4.5 14h7a2.5 2.5 0 0 0 2.5-2.5v-7A2.5 2.5 0 0 0 11.5 2h-7ZM3 4.5C3 3.67 3.67 3 4.5 3h7c.83 0 1.5.67 1.5 1.5v7c0 .83-.67 1.5-1.5 1.5h-7A1.5 1.5 0 0 1 3 11.5v-7Z" fill="currentColor"></path>
                </MetadataIcon>
                Text-generation
              </MetadataItem>
              <MetadataItem>
                <MetadataIcon viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
                  <path d="M7.2 2.1a.9.9 0 0 1 1.6 0l1.53 3.08 3.4.5a.9.9 0 0 1 .5 1.53l-2.46 2.4.58 3.39a.9.9 0 0 1-1.3.95L8 12.35l-3.04 1.6a.9.9 0 0 1-1.3-.95l.57-3.39-2.46-2.4a.9.9 0 0 1 .5-1.53l3.4-.5L7.2 2.1Zm.8.63L6.55 5.67a.9.9 0 0 1-.68.5l-3.25.47 2.35 2.3c.21.2.3.5.26.79l-.56 3.24 2.91-1.53a.9.9 0 0 1 .84 0l2.9 1.53-.55-3.24a.9.9 0 0 1 .26-.8l2.36-2.3-3.26-.46a.9.9 0 0 1-.67-.5L8 2.73Z" fill="currentColor"></path>
                </MetadataIcon>
                561
              </MetadataItem>
              <MetadataItem>
                <MetadataIcon viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5.75 3.06A.5.5 0 0 0 5 3.5v9c0 .38.41.62.75.44l8-4.5a.5.5 0 0 0 0-.88l-8-4.5ZM4 3.5a1.5 1.5 0 0 1 2.24-1.3l8 4.5a1.5 1.5 0 0 1 0 2.6l-8 4.5A1.5 1.5 0 0 1 4 12.5v-9Z" fill="currentColor"></path>
                </MetadataIcon>
                154
              </MetadataItem>
            </ModelMetadata>
          </ModelCard>

          <ModelCard size="small">
            <ModelHeader>
              <ModelIcon src="/assets/Mistral-07985112.svg" alt="Mistral" />
              <ModelName>mistralai-Mixtral-8x7B</ModelName>
              <ModelSeparator>•</ModelSeparator>
              <ModelStatus new>
                <StatusIcon viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5.47 9.83a.92.92 0 0 0 1.4-.46l.35-1.07A1.72 1.72 0 0 1 8.3 7.22l1.09-.35a.92.92 0 0 0-.04-1.75l-1.07-.35A1.7 1.7 0 0 1 7.2 3.7L6.85 2.6a.92.92 0 0 0-.87-.6c-.2 0-.38.05-.53.16a.92.92 0 0 0-.35.46l-.35 1.09A1.71 1.71 0 0 1 3.7 4.77l-1.08.36a.92.92 0 0 0 0 1.74l1.08.35A1.71 1.71 0 0 1 4.78 8.3l.35 1.08c.06.18.18.34.34.45ZM4 6.26 3.15 6l.87-.28A2.72 2.72 0 0 0 5.7 4l.26-.86.28.87a2.7 2.7 0 0 0 1.72 1.71l.88.27-.86.28a2.7 2.7 0 0 0-1.72 1.72l-.26.85-.28-.86A2.7 2.7 0 0 0 4 6.26Zm6.52 7.6c.14.09.3.14.47.14a.81.81 0 0 0 .76-.55l.25-.76a1.09 1.09 0 0 1 .68-.68l.77-.25a.8.8 0 0 0-.02-1.53l-.77-.25a1.08 1.08 0 0 1-.68-.67l-.25-.78A.8.8 0 0 0 11.1 8a.8.8 0 0 0-.88.54l-.25.77a1.1 1.1 0 0 1-.66.67l-.78.26a.8.8 0 0 0-.38 1.22c.1.14.23.24.4.3l.76.25a1.09 1.09 0 0 1 .68.68l.25.77c.06.16.16.3.3.4Zm-.91-2.8L9.44 11l.19-.06a2.09 2.09 0 0 0 1.3-1.32l.05-.18.06.18a2.08 2.08 0 0 0 1.32 1.32l.2.06-.18.06a2.08 2.08 0 0 0-1.32 1.32l-.06.18-.06-.18a2.07 2.07 0 0 0-1.32-1.32Z" fill="currentColor"></path>
                </StatusIcon>
                New
              </ModelStatus>
            </ModelHeader>
            <ModelMetadata>
              <MetadataItem>
                <MetadataIcon viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5.5 5a.5.5 0 0 0 0 1h5a.5.5 0 0 0 0-1h-5Zm0 2.5a.5.5 0 0 0 0 1h3a.5.5 0 0 0 0-1h-3Zm0 2.5a.5.5 0 0 0 0 1h5a.5.5 0 0 0 0-1h-5Zm-1-8A2.5 2.5 0 0 0 2 4.5v7A2.5 2.5 0 0 0 4.5 14h7a2.5 2.5 0 0 0 2.5-2.5v-7A2.5 2.5 0 0 0 11.5 2h-7ZM3 4.5C3 3.67 3.67 3 4.5 3h7c.83 0 1.5.67 1.5 1.5v7c0 .83-.67 1.5-1.5 1.5h-7A1.5 1.5 0 0 1 3 11.5v-7Z" fill="currentColor"></path>
                </MetadataIcon>
                Text-generation
              </MetadataItem>
              <MetadataItem>
                <MetadataIcon viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
                  <path d="M7.2 2.1a.9.9 0 0 1 1.6 0l1.53 3.08 3.4.5a.9.9 0 0 1 .5 1.53l-2.46 2.4.58 3.39a.9.9 0 0 1-1.3.95L8 12.35l-3.04 1.6a.9.9 0 0 1-1.3-.95l.57-3.39-2.46-2.4a.9.9 0 0 1 .5-1.53l3.4-.5L7.2 2.1Zm.8.63L6.55 5.67a.9.9 0 0 1-.68.5l-3.25.47 2.35 2.3c.21.2.3.5.26.79l-.56 3.24 2.91-1.53a.9.9 0 0 1 .84 0l2.9 1.53-.55-3.24a.9.9 0 0 1 .26-.8l2.36-2.3-3.26-.46a.9.9 0 0 1-.67-.5L8 2.73Z" fill="currentColor"></path>
                </MetadataIcon>
                561
              </MetadataItem>
              <MetadataItem>
                <MetadataIcon viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5.75 3.06A.5.5 0 0 0 5 3.5v9c0 .38.41.62.75.44l8-4.5a.5.5 0 0 0 0-.88l-8-4.5ZM4 3.5a1.5 1.5 0 0 1 2.24-1.3l8 4.5a1.5 1.5 0 0 1 0 2.6l-8 4.5A1.5 1.5 0 0 1 4 12.5v-9Z" fill="currentColor"></path>
                </MetadataIcon>
                154
              </MetadataItem>
            </ModelMetadata>
          </ModelCard>

          <ModelCard size="small">
            <ModelHeader>
              <ModelIcon src="https://download.logo.wine/logo/WooCommerce/WooCommerce-Logo.wine.png" alt="WooCommerce" style={{ width: '18px', height: '18px', objectFit: 'contain' }} />
              <ModelName>WooCommerce</ModelName>
              <ModelSeparator>•</ModelSeparator>
              <ModelStatus hot>
                <StatusIcon viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8.17 2.38c.28-.14.55-.24.8-.3.02.54.18 1.08.41 1.59a16.63 16.63 0 0 0 1.32 2.25c.46.7.9 1.38 1.24 2.06.35.7.56 1.38.56 2.02 0 1.15-.34 2.15-.99 2.85a3.92 3.92 0 0 1-3 1.15c-1.4 0-2.37-.4-3.05-1.03a4.84 4.84 0 0 1-1-5.43l.13.25c.38.76 1.3 1.07 2.07.7.84-.43 1.08-1.47.72-2.24-.37-.81-.66-1.81-.4-2.6.2-.58.65-1 1.19-1.27ZM4.1 6.18v.01l-.02.03a3.08 3.08 0 0 0-.22.33 5.33 5.33 0 0 0-.74 4.06c.26 1.19.77 2.3 1.65 3.1A5.3 5.3 0 0 0 8.5 15c1.62 0 2.89-.54 3.75-1.47A5.12 5.12 0 0 0 13.5 10c0-.86-.28-1.69-.67-2.46a21.42 21.42 0 0 0-1.35-2.25c-.47-.73-.9-1.4-1.19-2.03A2.77 2.77 0 0 1 10 1.6.5.5 0 0 0 9.5 1c-.42 0-1.12.15-1.79.5a3.3 3.3 0 0 0-1.68 1.84c-.4 1.17.06 2.5.44 3.32.17.38.02.79-.26.93a.54.54 0 0 1-.73-.24l-.53-1.07a.5.5 0 0 0-.84-.1Z" fill="currentColor"></path>
                </StatusIcon>
                Hot
              </ModelStatus>
            </ModelHeader>
            <ModelMetadata>
              <MetadataItem>
                <MetadataIcon viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5.5 5a.5.5 0 0 0 0 1h5a.5.5 0 0 0 0-1h-5Zm0 2.5a.5.5 0 0 0 0 1h3a.5.5 0 0 0 0-1h-3Zm0 2.5a.5.5 0 0 0 0 1h5a.5.5 0 0 0 0-1h-5Zm-1-8A2.5 2.5 0 0 0 2 4.5v7A2.5 2.5 0 0 0 4.5 14h7a2.5 2.5 0 0 0 2.5-2.5v-7A2.5 2.5 0 0 0 11.5 2h-7ZM3 4.5C3 3.67 3.67 3 4.5 3h7c.83 0 1.5.67 1.5 1.5v7c0 .83-.67 1.5-1.5 1.5h-7A1.5 1.5 0 0 1 3 11.5v-7Z" fill="currentColor"></path>
                </MetadataIcon>
                Order-Tracking
              </MetadataItem>
              <MetadataItem>
                <MetadataIcon viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
                  <path d="M7.2 2.1a.9.9 0 0 1 1.6 0l1.53 3.08 3.4.5a.9.9 0 0 1 .5 1.53l-2.46 2.4.58 3.39a.9.9 0 0 1-1.3.95L8 12.35l-3.04 1.6a.9.9 0 0 1-1.3-.95l.57-3.39-2.46-2.4a.9.9 0 0 1 .5-1.53l3.4-.5L7.2 2.1Zm.8.63L6.55 5.67a.9.9 0 0 1-.68.5l-3.25.47 2.35 2.3c.21.2.3.5.26.79l-.56 3.24 2.91-1.53a.9.9 0 0 1 .84 0l2.9 1.53-.55-3.24a.9.9 0 0 1 .26-.8l2.36-2.3-3.26-.46a.9.9 0 0 1-.67-.5L8 2.73Z" fill="currentColor"></path>
                </MetadataIcon>
                561
              </MetadataItem>
              <MetadataItem>
                <MetadataIcon viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
                  <path d="M5.75 3.06A.5.5 0 0 0 5 3.5v9c0 .38.41.62.75.44l8-4.5a.5.5 0 0 0 0-.88l-8-4.5ZM4 3.5a1.5 1.5 0 0 1 2.24-1.3l8 4.5a1.5 1.5 0 0 1 0 2.6l-8 4.5A1.5 1.5 0 0 1 4 12.5v-9Z" fill="currentColor"></path>
                </MetadataIcon>
                154
              </MetadataItem>
            </ModelMetadata>
          </ModelCard>
        </ServiceGrid>
      </ServiceGridContainer>

      <TextSection>
        <MainTitle>Your pages are the answer. We just makes them talk.</MainTitle>
        <Subtitle>From multilingual support to order tracking, SmushLabs brings together the essentials of modern AI chatbots. Built for small businesses that need clarity, not complexity.</Subtitle>
        <Disclaimer>*Integrations rolling out soon</Disclaimer>
        <ButtonContainer>
          <GetStartedButton href="/login" onClick={(e) => {
            // Handle login/signup
            e.preventDefault();
            // TODO: Implement login/signup modal or redirect
            console.log('Get Started clicked');
          }}>
            Get Started
          </GetStartedButton>
          <EnquireButton href="/contact" onClick={(e) => {
            // Handle enquire features
            e.preventDefault();
            // TODO: Implement contact form or redirect
            console.log('Enquire Features clicked');
          }}>
            Enquire Features
          </EnquireButton>
        </ButtonContainer>
      </TextSection>
    </MainContainer>
  );
};

export default AzureServiceGrid;
